/**
 * Componente Customers que permite llevar acabo todas las operaciones sobre los clientes
 */

import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useFirebase } from 'react-redux-firebase';
import { Button, Table, Spin, Modal, Form, Input, notification, Divider, Select, List, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';

import { customers as customersActions } from '../../actions/database/customers';
import { CUSTOMERS } from '../../../application/types';
import { matchesSearch } from '../../lib/text_search';
import { ModalAddUser } from './modal_add_user';
import { InboundConfigDrawer } from '../inbound/inbound_config_drawer';
import { CustomerPermissionsDrawer } from './permissions_drawer';
import { loadStaffUsers } from '../../lib/staff_users';
import { canShowPermissions, loadTargetRoles, filterAdjustableStaff } from '../../lib/target_rank';
import { canAdjust, canToggleDelete } from '../../lib/rank';

import '../styles/table.scss'

/**
 * 
 * Exporta el componente Customers
 */
export const Customers = () => {

  // Funcion que permite ejecutar todas las acciones del estado de redux
  const dispatch = useDispatch();
  // Selecciona los reductores
  const roles = useSelector(state => state.get('roles').get('roles').roles);
  const read = useSelector(state => state.get('customers').get('read'));

  // Manejo de los estados del componente
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  // Búsqueda en vivo (client-side): campo + texto
  const [searchField, setSearchField] = useState('all');
  const [searchText, setSearchText] = useState('');
  const [update, setUpdate] = useState(false);
  const [id, setId] = useState("");
  const [users, setUsers] = useState({ visible: false, loading: false, id: "" });
  const [table, setTable] = useState({ loading: false });
  // Estado del drawer de configuración de bandeja de email por cliente
  const [inboundConfig, setInboundConfig] = useState({ open: false, customerId: null, customerName: '' });
  // Estado del drawer de permisos por usuario dentro de un cliente
  const [userPerms, setUserPerms] = useState({ open: false, customerId: null, customerName: '', user: null });

  // Personal global: se le ajustan permisos por empresa SIN vincularlo (spec 2026-10-06).
  // Listarlo exige leer `users` (reglas: isSuperAdmin → level 'all' sin alcance).
  const firebase = useFirebase();
  const canListStaff = roles?.documents?.level === 'all';
  const STAFF_PICK_CLOSED = { open: false, loading: false, error: false, record: null, list: [], uid: undefined };
  const [staffPick, setStaffPick] = useState(STAFF_PICK_CLOSED);
  const openStaffPick = ( record ) => {
    setStaffPick({ ...STAFF_PICK_CLOSED, open: true, loading: true, record });
    const memberUids = new Set( ( record.users || [] ).map( u => u?.reference?.id ).filter( Boolean ) );
    loadStaffUsers( firebase.firestore(), { memberUids } )
      .then( list => setStaffPick( s => ({ ...s, loading: false, list: filterAdjustableStaff({ list, viewerRole: roles }) }) ) )
      .catch( () => setStaffPick( s => ({ ...s, loading: false, error: true }) ) );
  };

  // Rol de cada usuario de las filas (rango, spec 2026-10-07 §4): el botón
  // «Permisos» y el de administrador solo aparecen si quien mira tiene MÁS rango.
  // Sale del espejo público usuarios_rol; se lee al expandir la empresa y un rol
  // ilegible queda en null (no muestra los botones).
  const targetRolesRef = useRef(new Map());
  const [targetRoles, setTargetRoles] = useState(new Map());
  const ensureTargetRoles = ( users ) => {
    const uids = ( users || [] ).map( u => u?.reference?.id ).filter( Boolean );
    if ( !uids.some( uid => !targetRolesRef.current.has( uid ) ) ) return;
    loadTargetRoles({ db: firebase.firestore(), uids, known: targetRolesRef.current }).then( map => {
      // Se UNE con lo vigente (otra fila pudo expandirse mientras tanto) y se
      // ignora el resultado si el componente ya se desmontó.
      if ( !aliveRef.current ) return;
      const merged = new Map([ ...targetRolesRef.current, ...map ]);
      targetRolesRef.current = merged;
      setTargetRoles( merged );
    });
  };
  const aliveRef = useRef(true);
  useEffect(() => {
    aliveRef.current = true;
    return () => { aliveRef.current = false; };
  }, []);

  // Hook para el control de los formularios
  const [form] = Form.useForm();

  /**
   * Metodo del componente que permite crear un cliente
   */
  const onCreate = values => {
    setLoading(true);
    dispatch( customersActions.create({ roles: roles.customers, nit: values.nit, name: values.name }) ).then( () => {
      setVisible(false);
      setLoading(false);
      form.resetFields();
      dispatch( customersActions.read({ roles: roles.customers }) );
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de crear el cliente',
        placement:  'topRight'
      });
      setVisible(false);
      setLoading(false);
    });
  };

  /**
   * Metodo del componente que permite actualizar un cliente
   * @param {object} record 
   */
  const onUpdate = values => {
    setLoading(true); 
    dispatch(
      customersActions.update({
        roles: roles.customers,
        data: { id, fields: { 'Razon social': values.name, 'NIT': values.nit } }
      })
    ).then( () => {
      setVisible(false);
      setLoading(false);
      form.resetFields();
      dispatch( customersActions.read({ roles: roles.customers }) );
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de actualizar el cliente',
        placement:  'topRight'
      });
      setVisible(false);
      setLoading(false);
    });
  };

  /**
   * Metodo del componente que permite eliminar un cliente
   * @param {object} record 
   */
  const onDelete = record => {
    dispatch( customersActions.delete({ roles: roles.customers, id: record.key }) ).then( () => {
      form.resetFields();
      dispatch( customersActions.read({ roles: roles.customers }) );
    })
    .catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de eliminar el cliente',
        placement:  'topRight'
      });
    });
  };

  /**
   * Metodo del componente que permite asignar un usuario a un cliente
   */
  const onAddUser = ({values, onError}) => {

    const id = users.id;
    setUsers({ visible: true, loading: true, id: "" });

    dispatch( customersActions.addUser({ roles: roles.customers, id, email: values.email }) ).then( () => {
      form.resetFields();
      setUsers({ visible: false, loading: false, id: "" });
      dispatch( customersActions.read({ roles: roles.customers }) );
    }).catch( error => {
      onError(error);
      setUsers({ visible: true, loading: false, id });
    });

  };

  /**
   * Metodo del componente que permite retirar un usuario de un cliente
   * @param {object} record 
   * @param {object} item 
   */
  const onRemoveUser = ( record, item ) => {
    dispatch( customersActions.removeUser({ roles: roles.customers, id: record.key, email: item.email }) ).then( () => {
      form.resetFields();
      dispatch( customersActions.read({ roles: roles.customers }) );
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: 'Se presento un error al tratar de quitar el usuario',
        placement:  'topRight'
      });
    });
  }

  /**
   * Metodo del componente que permite asignar un usuario como administrador a un cliente
   * @param {object} record 
   * @param {object} item 
   */
  const setUserAdmin = ( record, item ) => {

    setTable({ loading: true });
    dispatch( customersActions.setUserAdmin({ roles: roles.customers, id: record.key, item }) ).then( () => {

      form.resetFields();
      dispatch( customersActions.read({ roles: roles.customers }) ).then( () => {
        setTable({ loading: false });
      }).catch( error => {
        notification.error({
          message: 'Error',
          description: 'Se presento un error al tratar de actulizar el usuario',
          placement:  'topRight'
        });
        setTable({ loading: false });
      });
    
    }).catch( error => {
      notification.error({
        message: 'Error',
        description: error?.message || 'Se presento un error al tratar de actulizar el usuario',
        placement:  'topRight'
      });
      setTable({ loading: false });
    });

  };

  // Ejecuta la funcion una vez termina de renderizar el componente
  useEffect(() => {
    dispatch( customersActions.read({ roles: roles.customers }) );
  }, [dispatch, roles.customers]);

  // Filas de la tabla a partir del estado de redux (lista completa)
  const rows = useMemo(() => (
    read?.customers?.map( customer => ({
      key: customer.id,
      name: customer.data['Razon social'],
      nit: customer.data['NIT'],
      users: customer.data['Usuarios']
    })) || []
  ), [read]);

  // Búsqueda en vivo: razón social, NIT o email de un usuario asociado,
  // insensible a mayúsculas/tildes y por palabras en cualquier orden
  const filtered = useMemo(() => {
    if (!searchText.trim()) return rows;
    return rows.filter( row => {
      const userEmails = (row.users || []).map( u => u.email ).join(' ');
      const haystack = searchField === 'all' ? `${row.name} ${row.nit} ${userEmails}`
        : searchField === 'users' ? userEmails
        : row[searchField];
      return matchesSearch(haystack, searchText);
    });
  }, [rows, searchText, searchField]);

  const SEARCH_PLACEHOLDERS = {
    all: 'Buscar por razón social, NIT o email de usuario asociado...',
    name: 'Buscar por razón social...',
    nit: 'Buscar por NIT...',
    users: 'Buscar empresas por email de usuario asociado...'
  };

  /**
   * Metodo que rederiza el componente
   */
  return (
    <div>

      <div style = {{ marginBottom: 15, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }} >
        <Input
          allowClear
          value = { searchText }
          onChange = { e => setSearchText(e.target.value) }
          placeholder = { SEARCH_PLACEHOLDERS[searchField] }
          prefix = { <SearchOutlined style = {{ color: 'rgba(0,0,0,.25)' }} /> }
          style = {{ flex: 1, minWidth: 260 }}
          addonBefore = { (
            <Select
              value = { searchField }
              style = {{ width: 160 }}
              onSelect = { value => setSearchField(value) } >
              <Select.Option value = "all" > Todos los campos </Select.Option>
              <Select.Option value = "name" > Razon social </Select.Option>
              <Select.Option value = "nit" > Documento (NIT) </Select.Option>
              <Select.Option value = "users" > Usuario asociado </Select.Option>
            </Select>
          )} />
        { searchText.trim() ?
            <Typography.Text type = "secondary" >
              { filtered.length } de { rows.length } empresas
            </Typography.Text> : null }
      </div>

      { read?.type === CUSTOMERS.READ.READED_CUSTOMERS ?
          <Table bordered size = 'middle' loading = { table.loading }
            locale = {{ emptyText: 'La lista de clientes se encuentra vacia' }}
            title = { () => (
              roles?.customers?.create ?
                <Button type = 'dashed'
                  disabled = { read?.type !== CUSTOMERS.READ.READED_CUSTOMERS }
                  onClick = { () => {
                    form.resetFields();
                    setVisible(true);
                    setUpdate(false);
                  }}
                > Nuevo cliente </Button> : null
            )}
            columns = {[
              { title: 'Razon social', dataIndex: 'name', key: 'name' },
              { title: 'NIT', dataIndex: 'nit', key: 'nit' },
              { title: 'Acciones', key: 'action',
                render: ( text, record ) => (
                  <span>
                    
                    { roles?.customers?.update ?
                        <>
                          <Button type = "link"
                            onClick = { () => {
                              form.resetFields();
                              setVisible(true);
                              setUpdate(true);
                              setId(record.key);
                              form.setFieldsValue({ 
                                nit: record.nit,
                                name: record.name
                              });
                            }}
                          > Editar </Button>
                          <Divider type = "vertical" />
                        </> : null }

                    { roles?.customers?.delete ?
                        <>
                          <Button type = "link"
                            onClick = { () => {
                              Modal.confirm({
                                title: `Eliminar cliente ${ record.name }`,
                                content: 'Esta seguro de eliminar el cliente seleccionado?',
                                okText: 'Eliminar',
                                okType: 'danger',
                                cancelText: 'Cancelar',
                                onOk: () => onDelete( record )
                              })
                            }}
                          > Eliminar </Button>
                          <Divider type = "vertical" />
                        </> : null }

                    { roles?.customers?.update ?
                        <Button type = "link"
                          onClick = { () => setInboundConfig({ open: true, customerId: record.key, customerName: record.name }) }
                        > Bandeja de email </Button> : null }

                  </span>
                )
              }
            ]}
            dataSource = { filtered }
            pagination = {{
              showTotal: ( total, range ) => `${range[0]}-${range[1]} de ${total} empresas`
            }}
            onExpand = { ( expanded, record ) => { if ( expanded ) ensureTargetRoles( record.users ); } }
            expandedRowRender = {
              record => (
                <List itemLayout = "vertical" size = "small" bordered = { true }
                  locale = {{ emptyText: `No hay usuarios para ${ record.name }` }}
                  header = {
                    <>
                      { roles?.customers?.create ?
                        <Button type = 'dashed'
                          disabled = { read?.type !== CUSTOMERS.READ.READED_CUSTOMERS }
                          onClick = { () => {
                            form.resetFields();
                            setUsers({ visible: true, loading: false, id: record.key });
                          }}
                        > Agregar usuario </Button> : null }
                      { roles?.customers?.update && canListStaff ?
                        <Button type = 'dashed' style = {{ marginLeft: 8 }} onClick = { () => openStaffPick( record ) } >
                          Permisos de personal global
                        </Button> : null }
                    </>
                  }
                  dataSource = { record.users }
                  renderItem = { item => (
                    <List.Item
                      actions = {[
                        roles?.customers?.delete ?
                          <Button type = "link"
                            onClick = { () => {
                              Modal.confirm({
                                title: `Quitar usuario ${ item.email }`,
                                content: 'Esta seguro de quitar el usuario seleccionado?',
                                okText: 'Quitar',
                                okType: 'danger',
                                cancelText: 'Cancelar',
                                onOk: () => onRemoveUser( record, item )
                              })
                            }}
                          > Quitar </Button> : null,
                        roles?.customers?.update && canToggleDelete( roles ) && canAdjust( roles, targetRoles.get( item.reference?.id ) )
                          ? <Button type = "link"
                              onClick = { () => {
                                Modal.confirm({
                                  title: `${ item.isAdmin ? 'Quitar' : 'Agregar' } usuario ${ item.email } como administrador`,
                                  content: `Esta seguro de ${ item.isAdmin ? 'quitar' : 'agregar' } el usuario seleccionado como administrador?`,
                                  okText: `${ item.isAdmin ? 'Quitar' : 'Agregar' }`,
                                  okType: 'danger',
                                  cancelText: 'Cancelar',
                                  onOk: () => setUserAdmin( record, item )
                                })
                              }}
                            > { item.isAdmin ? 'Quitar' : 'Agregar' } como administrador </Button>
                          : null,
                        roles?.customers?.update && canShowPermissions( roles, targetRoles.get( item.reference?.id ) )
                          ? <Button type = "link"
                              onClick = { () => setUserPerms({
                                open: true,
                                customerId: record.key,
                                customerName: record.name,
                                user: item
                              }) }
                            > Permisos </Button>
                          : null
                      ]} > { item.email } </List.Item>
                  )} />
              )
            }
          /> : read?.type === CUSTOMERS.READ.READING_CUSTOMERS ? <div style = {{ textAlign: 'center' }} > <Spin /> </div> : null }
      
      { roles?.customers?.create || roles?.customers?.update ?
          <Modal
            forceRender
            visible = { visible }
            confirmLoading = { loading }
            title = { !update ? "Nuevo cliente" : "Editar cliente" }
            okText = { !update ? "Crear" : "Editar" }
            cancelText = "Cancelar"
            maskClosable = { false }
            closable = { false }
            onOk = { () => form.submit() }
            onCancel = { () => setVisible(false) }
          >
            <Form name = "create_update_customer" form = { form } layout = "vertical" onFinish = { !update ? onCreate : onUpdate } >
              <Form.Item name = "nit" label = "Nit"
                rules = {[{ required: true, message: 'El numero de nit es requerido' }]} >
                <Input placeholder = 'NIT'  />
              </Form.Item>
              <Form.Item name = "name" label = "Razon social"
                rules = {[{ required: true, message: 'La razon social es requerida' }]} >
                <Input placeholder = 'razon social' />
              </Form.Item>
            </Form>
          </Modal> : null }

      { roles?.customers?.create ?
          <ModalAddUser
            users = { users }
            onAddUser = { onAddUser }
            onCancel = { () =>  setUsers({ visible: false, loading: false, id: "" }) } />
        : null }

      <InboundConfigDrawer
        customerId = { inboundConfig.customerId }
        customerName = { inboundConfig.customerName }
        open = { inboundConfig.open }
        onClose = { () => setInboundConfig({ open: false, customerId: null, customerName: '' }) }
      />

      <Modal
        visible = { staffPick.open }
        title = { `Personal global — ${ staffPick.record?.name || '' }` }
        okText = "Ver permisos"
        cancelText = "Cancelar"
        okButtonProps = {{ disabled: !staffPick.uid }}
        onCancel = { () => setStaffPick( STAFF_PICK_CLOSED ) }
        onOk = { () => {
          const s = staffPick.list.find( x => x.uid === staffPick.uid );
          if ( !s ) return;
          setUserPerms({ open: true, customerId: staffPick.record.key, customerName: staffPick.record.name, user: { email: s.email, reference: { id: s.uid }, staff: true } });
          setStaffPick( STAFF_PICK_CLOSED );
        }}
      >
        <p>Ajusta los permisos de esta persona solo en esta empresa, sin vincularla como usuario.</p>
        { staffPick.error ? <p style = {{ color: '#cf1322' }}>No se pudo cargar el personal global.</p> : null }
        <Select showSearch style = {{ width: '100%' }} loading = { staffPick.loading } placeholder = "Seleccione una persona"
          notFoundContent = "No hay personal global con un rango menor al suyo"
          optionFilterProp = "children" value = { staffPick.uid } onChange = { uid => setStaffPick( s => ({ ...s, uid }) ) } >
          { staffPick.list.map( s => <Select.Option key = { s.uid } value = { s.uid } > { s.email } </Select.Option> ) }
        </Select>
      </Modal>

      <CustomerPermissionsDrawer
        open = { userPerms.open }
        customerId = { userPerms.customerId }
        customerName = { userPerms.customerName }
        user = { userPerms.user }
        onClose = { () => setUserPerms({ open: false, customerId: null, customerName: '', user: null }) }
      />

    </div>
  )

};